import { Avatar } from "@dash/avatar";
import { Button } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { SectionHeader } from "@dash/page";
import { SearchButton } from "@dash/search";
import { DocsSelectDialog } from "@dash/select";
import { useTheme } from "@emotion/react";
import { useProfiles } from "@megarax/features";
import React, { useContext, useEffect, useState } from "react";
import { MdClose, MdOutlineGroupAdd } from "react-icons/md";
import { useQueryClient } from "react-query";

import { IamAuthContext } from "@megaron/auth-react";
import { useClientManager } from "@megaron/react-clients";
import { ResourceId } from "@megaron/resource";
import { Priority, TicketDetailDto } from "@megaron/support-contracts";

import { CrmCustomerContacts } from "../crm/CrmCustomerContacts";
import { useResolvedCrmCustomerId } from "../crm/useResolvedCrmCustomerId";
import { SupportTicketInvoiceNumberField } from "../invoice/SupportTicketInvoiceNumberField";
import { SupportPrioritySelect } from "../shared/SupportPrioritySelect";
import { SupportStatusSelect } from "../shared/SupportStatusSelect";
import { SupportTicketCategorySelect } from "../shared/SupportTicketCategorySelect";
import { SupportTicketRelatedTickets } from "./SupportTicketRelatedTickets";

type Props = {
  ticket: TicketDetailDto;
  listPath?: string;
};

export function updateTicketPayload(
  ticket: TicketDetailDto,
  patch: {
    subject?: string;
    status?: TicketDetailDto["status"];
    priority?: TicketDetailDto["priority"] | null;
    category?: TicketDetailDto["category"] | null;
    tags?: string[] | null;
    invoiceNumber?: string | null;
  },
) {
  return {
    id: ticket.id,
    subject: patch.subject ?? ticket.subject,
    status: patch.status ?? ticket.status,
    priority: patch.priority !== undefined ? patch.priority : ticket.priority ?? null,
    category: patch.category !== undefined ? patch.category : ticket.category ?? null,
    tags: patch.tags !== undefined ? patch.tags : ticket.tags,
    invoiceNumber:
      patch.invoiceNumber !== undefined ? patch.invoiceNumber : ticket.invoiceNumber ?? null,
  };
}

export const SupportTicketMetaSection: React.FC<Props> = ({ ticket, listPath = "/support/tickets" }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const auth = useContext(IamAuthContext);
  const support = useClientManager("support");
  const resolvedCustomerId = useResolvedCrmCustomerId(ticket);
  const updateMutation = support.updateTicket().useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
    },
  });
  const updateAssigneesMutation = support.updateTicketAssignees().useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
    },
  });
  const { isDesktop } = useDeviceType();
  const [assigneeDialogOpen, setAssigneeDialogOpen] = useState(false);
  const [invoiceNumberDraft, setInvoiceNumberDraft] = useState(ticket.invoiceNumber ?? "");

  useEffect(() => {
    setInvoiceNumberDraft(ticket.invoiceNumber ?? "");
  }, [ticket.invoiceNumber]);

  const { profile } = useProfiles();
  const isBusy = "isLoading" in updateMutation ? updateMutation.isLoading : false;
  const isAssigneesBusy = "isLoading" in updateAssigneesMutation ? updateAssigneesMutation.isLoading : false;
  const userEmail = auth.iamUser?.userType === "megarax" ? auth.iamUser.email : undefined;
  const assigneeEmails = ticket.assignees?.map((a) => a.email) ?? [];
  const isAssignee = !!userEmail && assigneeEmails.includes(userEmail);

  const handleStatusChange = (status: TicketDetailDto["status"]) => {
    updateMutation.mutate(updateTicketPayload(ticket, { status }));
  };
  const handlePriorityChange = (priority: Priority | null) => {
    updateMutation.mutate(updateTicketPayload(ticket, { priority }));
  };
  const handleCategoryChange = (category: TicketDetailDto["category"] | null) => {
    updateMutation.mutate(updateTicketPayload(ticket, { category }));
  };
  const commitInvoiceNumber = () => {
    const next = invoiceNumberDraft.trim() || null;
    if (next === (ticket.invoiceNumber ?? null)) return;
    updateMutation.mutate(updateTicketPayload(ticket, { invoiceNumber: next }));
  };
  const setAssignees = (emails: string[]) => {
    updateAssigneesMutation.mutate({ id: ticket.id, assigneeEmails: emails });
  };
  const removeAssignee = (email: string) => {
    const next = (ticket.assignees?.map((a) => a.email) ?? []).filter((e) => e !== email);
    updateAssigneesMutation.mutate({ id: ticket.id, assigneeEmails: next });
  };
  const isFormDisabled = !!isBusy || !!isAssigneesBusy;
  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {!isDesktop && (
        <div css={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
          <div css={{ display: "flex", alignItems: "center", gap: "0.25rem", minWidth: 0 }}>
            {ticket.assignees?.slice(0, 4).map((assignee) => {
              const userProfile = profile(assignee.email);
              return <Avatar key={assignee.email} src={userProfile?.profilePictureUrl ?? null} />;
            })}
            <button
              onClick={() => setAssigneeDialogOpen(true)}
              css={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "100%",
                border: "none",
                padding: "0.25rem",
                width: "30px",
                height: "30px",
                background: theme.colors.primaryLight,
                flexShrink: 0,
              }}
            >
              <MdOutlineGroupAdd size={16} css={{ color: theme.colors.primary }} />
            </button>
          </div>
          <div css={{ minWidth: "9rem" }}>
            <SupportStatusSelect value={ticket.status} onChange={handleStatusChange} isDisabled={isFormDisabled} />
          </div>
        </div>
      )}
      {isDesktop && (
        <SupportStatusSelect value={ticket.status} onChange={handleStatusChange} isDisabled={isFormDisabled} />
      )}
      <SupportPrioritySelect value={ticket.priority} onChange={handlePriorityChange} isDisabled={isFormDisabled} />
      <SupportTicketCategorySelect
        value={ticket.category}
        onChange={handleCategoryChange}
        isDisabled={isFormDisabled}
      />
      {isDesktop && (
        <>
          <SectionHeader
            isHr
            css={{ h2: { fontSize: "16px" } }}
            actions={
              !isAssignee &&
              userEmail && (
                <Button
                  size="small"
                  variant="outline"
                  isLoading={isAssigneesBusy}
                  onClick={() => setAssignees([...new Set([...assigneeEmails, userEmail])])}
                >
                  Dołącz
                </Button>
              )
            }
          >
            Uczestnicy
          </SectionHeader>
          <div css={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div css={{ display: "flex", flexDirection: "column", padding: "0 8px", gap: "10px" }}>
              {ticket.assignees?.map((a) => {
                const userProfile = profile(a.email);
                const displayName = userProfile
                  ? `${userProfile.firstName} ${userProfile.lastName}`
                  : a.email;
                return (
                  <div css={{ display: "flex", gap: "10px", alignItems: "center" }} key={a.email}>
                    <Avatar src={userProfile?.profilePictureUrl ?? null} />
                    <span css={{ fontSize: "14px", fontWeight: 400 }}>{displayName}</span>
                    <button
                      type="button"
                      onClick={() => removeAssignee(a.email)}
                      css={{
                        marginLeft: "auto",
                        padding: 4,
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        color: theme.colors.secondaryText,
                      }}
                    >
                      <MdClose size={18} />
                    </button>
                  </div>
                );
              })}
            </div>
            <SearchButton
              variant="pale"
              onClick={() => setAssigneeDialogOpen(true)}
              inputText="Przypisz użytkownika..."
            />
          </div>
        </>
      )}
      <SupportTicketInvoiceNumberField
        value={invoiceNumberDraft}
        onChange={setInvoiceNumberDraft}
        onBlur={commitInvoiceNumber}
        isDisabled={isFormDisabled}
      />
      <DocsSelectDialog
        docType={["user"]}
        variant="multi-select"
        label="Przypisz użytkowników"
        initiallySelectedDocIds={ticket.assignees?.map((a) => ResourceId("user", a.email)) ?? []}
        noTrigger
        isOpen={assigneeDialogOpen}
        onClose={() => setAssigneeDialogOpen(false)}
        onSelectedChange={(chosen) => {
          const emails = chosen
            .map((o) => o?.value)
            .filter((email): email is string => !!email);
          setAssignees(emails);
          setAssigneeDialogOpen(false);
        }}
      />
      {isDesktop && <SupportTicketRelatedTickets ticket={ticket} listPath={listPath} />}
      {
        isDesktop && resolvedCustomerId && (
          <>
            <SectionHeader isHr css={{ h2: { fontSize: "16px" } }}>
              Kontakty
            </SectionHeader>
            <CrmCustomerContacts customerId={resolvedCustomerId} />
          </>
        )
      }
    </div>
  );
};
